import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import CloseModal from "../../../components/Atomos/Icons/EditMode/CloseModal";
import SaveChanges from "../../../components/Atomos/Icons/EditMode/SaveChanges";
import Drag_pan from "../../../components/Atomos/Icons/Drag_pan";
import Button from "../../../components/staff/Button";
import CheckBox from "../../../components/staff/CheckBox";
import DropDownMultiple from "../../../components/staff/DropDownMultiple";
import ProfilePhoto from "../../../components/Atomos/ProfilePhoto";
import type { IOfficeNew } from "../../../interfaces/models/IOffice";
import type {
  IContactInfo,
  IOfficeMainContact,
} from "../../../interfaces/models/IOfficeMainContact";
import { fetchPersonByFirm } from "../../../Services/Person";
import {
  getOfficeMainContactsByFirmId,
  saveOfficeMainContact,
} from "../../../Services/OfficeMainContact";
import {
  mergeOfficeMainContactsWithOffices,
  officeLabelForMainContact,
  personToContactInfo,
  serializeOfficeMainContacts,
  toOfficeMainContactWritePayload,
  validateOfficeMainContactList,
  type PersonForMainContact,
} from "../../../utils/officeMainContactUtils";
import { toastError, toastPromise } from "../../../utils/toast";
import { useStaffMode } from "../../../hooks/StaffModeProvider";
import type { PersonOfficeLike } from "./manageDataAdminOfficeRules";

type OfficeOption = { id: string; name: string };
type PersonOption = { id: string; name: string };

type SortableContactCardProps = {
  contact: IContactInfo;
  isPrimary: boolean;
  onDelete: () => void;
  profilePath?: string;
};

function SortableContactCard({
  contact,
  isPrimary,
  onDelete,
}: SortableContactCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: contact.contactId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex min-w-[220px] max-w-[260px] shrink-0 flex-col gap-3 rounded-md border border-gray-100 bg-white p-4 shadow-sm"
    >
      <div className="flex items-center justify-between gap-2">
        {isPrimary ? (
          <span className="text-xs font-semibold uppercase tracking-wide text-primary">
            Primary Contact
          </span>
        ) : (
          <span className="text-xs text-gray-300">&nbsp;</span>
        )}
        <button
          type="button"
          className="cursor-grab touch-none text-gray-400 active:cursor-grabbing"
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <Drag_pan className="h-5 w-5" />
        </button>
      </div>
      <div className="flex flex-col items-center gap-2 text-center">
        <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-gray-100">
          <ProfilePhoto src={contact.photoUrl} />
        </div>
        <span className="text-base font-semibold text-primary">{contact.name}</span>
      </div>
      <div className="text-center text-sm text-gray-font">
        <div className="break-all">{contact.email || "—"}</div>
        <div>{contact.phone || "—"}</div>
      </div>
      <button
        type="button"
        className="text-sm font-medium text-red-600 hover:underline"
        onClick={onDelete}
      >
        Delete
      </button>
    </div>
  );
}

export type ManageOfficeMainContactsModalProps = {
  firmId: string;
  offices: IOfficeNew[];
  officeMainContactData: IOfficeMainContact[];
  initialOfficeId?: string;
  onClose?: () => void;
  onSaved?: (data: IOfficeMainContact[]) => void;
};

const ManageOfficeMainContactsModal = ({
  firmId,
  offices,
  officeMainContactData,
  initialOfficeId,
  onClose,
  onSaved,
}: ManageOfficeMainContactsModalProps) => {
  const { isStaffMode } = useStaffMode();
  const mergedDocuments = useMemo(
    () => mergeOfficeMainContactsWithOffices(officeMainContactData, offices, firmId),
    [officeMainContactData, offices, firmId],
  );

  const officeOptions: OfficeOption[] = useMemo(
    () =>
      offices.map((office) => ({
        id: String(office.id ?? "").trim(),
        name: officeLabelForMainContact(office),
      })),
    [offices],
  );

  const [selectedOfficeId, setSelectedOfficeId] = useState(
    () =>
      String(initialOfficeId ?? "").trim() ||
      officeOptions[0]?.id ||
      "",
  );

  useEffect(() => {
    const next = String(initialOfficeId ?? "").trim();
    if (next) {
      setSelectedOfficeId(next);
    }
  }, [initialOfficeId]);

  const [draftsByOffice, setDraftsByOffice] = useState<
    Record<string, IContactInfo[]>
  >(() => {
    const initial: Record<string, IContactInfo[]> = {};
    for (const doc of mergedDocuments) {
      const oid = String(doc.officeId ?? "").trim();
      initial[oid] = [...(doc.contacts ?? [])];
    }
    return initial;
  });

  const [originalSnapshots] = useState<Record<string, string>>(() => {
    const snap: Record<string, string> = {};
    for (const doc of mergedDocuments) {
      const oid = String(doc.officeId ?? "").trim();
      snap[oid] = serializeOfficeMainContacts(doc.contacts ?? []);
    }
    return snap;
  });

  const [persons, setPersons] = useState<PersonOfficeLike[]>([]);
  const [selectedPerson, setSelectedPerson] = useState<PersonOption | null>(
    null,
  );
  const [addToAllFirmOffices, setAddToAllFirmOffices] = useState(false);

  useEffect(() => {
    setAddToAllFirmOffices(false);
  }, [selectedPerson?.id]);

  const allOfficeIds = useMemo(
    () => officeOptions.map((office) => office.id).filter(Boolean),
    [officeOptions],
  );

  useEffect(() => {
    if (!firmId) {
      return;
    }
    let mounted = true;
    void fetchPersonByFirm(firmId).then((data) => {
      if (mounted) {
        setPersons(Array.isArray(data) ? data : []);
      }
    });
    return () => {
      mounted = false;
    };
  }, [firmId]);

  const personOptions: PersonOption[] = useMemo(
    () =>
      persons
        .map((person) => {
          const id = String(person.id ?? "").trim();
          if (!id) {
            return null;
          }
          const name =
            `${person.name ?? ""} ${person.lastName ?? ""}`.trim() || id;
          return { id, name };
        })
        .filter((entry): entry is PersonOption => entry != null)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [persons],
  );

  const activeContacts = draftsByOffice[selectedOfficeId] ?? [];

  const setActiveContacts = useCallback(
    (updater: (current: IContactInfo[]) => IContactInfo[]) => {
      setDraftsByOffice((prev) => ({
        ...prev,
        [selectedOfficeId]: updater(prev[selectedOfficeId] ?? []),
      }));
    },
    [selectedOfficeId],
  );

  const selectedOfficeLabel =
    officeOptions.find((o) => o.id === selectedOfficeId)?.name ?? "Office";

  const canAddContact =
    selectedPerson != null &&
    !activeContacts.some((c) => c.contactId === selectedPerson.id);

  const handleAddContact = () => {
    if (!selectedPerson || !canAddContact) {
      return;
    }
    const person = persons.find(
      (p) => String(p.id ?? "").trim() === selectedPerson.id,
    );
    if (!person) {
      return;
    }

    const contactId = String(person.id ?? "").trim();

    if (addToAllFirmOffices) {
      setDraftsByOffice((prev) => {
        const next = { ...prev };
        for (const officeId of allOfficeIds) {
          const list = [...(next[officeId] ?? [])];
          if (list.some((c) => c.contactId === contactId)) {
            continue;
          }
          next[officeId] = [
            ...list,
            personToContactInfo(person as PersonForMainContact, officeId),
          ];
        }
        return next;
      });
    } else {
      const entry = personToContactInfo(
        person as PersonForMainContact,
        selectedOfficeId,
      );
      setActiveContacts((list) => [...list, entry]);
    }

    setSelectedPerson(null);
    setAddToAllFirmOffices(false);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    setActiveContacts((items) => {
      const oldIndex = items.findIndex((c) => c.contactId === active.id);
      const newIndex = items.findIndex((c) => c.contactId === over.id);
      if (oldIndex < 0 || newIndex < 0) {
        return items;
      }
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const handleSave = async () => {
    for (const officeId of allOfficeIds) {
      const validationError = validateOfficeMainContactList(
        draftsByOffice[officeId] ?? [],
      );
      if (validationError) {
        toastError(validationError);
        return;
      }
    }

    const changedOfficeIds = allOfficeIds.filter((officeId) => {
      const contacts = draftsByOffice[officeId] ?? [];
      return (
        serializeOfficeMainContacts(contacts) !==
        (originalSnapshots[officeId] ?? serializeOfficeMainContacts([]))
      );
    });

    if (changedOfficeIds.length === 0) {
      onClose?.();
      return;
    }

    await toastPromise(
      (async () => {
        for (const officeId of changedOfficeIds) {
          const contacts = draftsByOffice[officeId] ?? [];
          const baseDoc =
            mergedDocuments.find(
              (doc) => String(doc.officeId ?? "").trim() === officeId,
            ) ??
            ({
              firmId,
              officeId,
              contacts: [],
            } as IOfficeMainContact);

          const payload = toOfficeMainContactWritePayload({
            ...baseDoc,
            contacts,
          });
          await saveOfficeMainContact(payload);
        }
        return getOfficeMainContactsByFirmId(firmId);
      })(),
      {
        pending: "Saving main contacts…",
        success: "Main contacts saved",
      },
    )
      .then((refreshed) => {
        onSaved?.(refreshed);
        onClose?.();
      })
      .catch(() => undefined);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <span className="text-sm font-semibold text-gray-font">1. Select Office</span>
        <DropDownMultiple<OfficeOption>
          title="Office"
          options={officeOptions}
          value={
            officeOptions.find((o) => o.id === selectedOfficeId) ?? null
          }
          placeholder="Select an office"
          searchable
          renderInPortal
          onChange={(option) => {
            if (option) {
              setSelectedOfficeId(String(option.id));
            }
          }}
        />
      </div>

      <div className="flex flex-col gap-3">
        <span className="text-sm font-semibold text-gray-font">
          2. Add Professional
        </span>
        <div className="flex flex-col gap-3 md:flex-row md:items-end">
          <div className="min-w-0 flex-1">
            <DropDownMultiple<PersonOption>
              title="Search by name"
              options={personOptions}
              value={selectedPerson}
              placeholder="Search by name (e.g. Laura, David…)"
              searchable
              renderInPortal
              onChange={setSelectedPerson}
            />
          </div>
          <Button
            text="Add Contact"
            color={isStaffMode ? "secondary" : "primary"}
            variant="filled"
            size="fixed"
            disabled={!canAddContact}
            onClick={handleAddContact}
          />
        </div>
        <CheckBox
          checked={addToAllFirmOffices}
          disabled={!selectedPerson}
          onCheckedChange={setAddToAllFirmOffices}
          label={
            <>
              Add as contact in <span className="font-semibold">all</span> firm
              offices
            </>
          }
          labelClassName="text-sm"
        />
      </div>

      <div className="flex flex-col gap-3">
        <span className="text-sm font-semibold text-gray-font">
          3. Assigned Contacts ({selectedOfficeLabel})
        </span>
        <p className="text-xs text-gray-500">
          Drag and drop cards to reorder. The first card is always the Primary
          Contact.
        </p>

        {activeContacts.length === 0 ? (
          <p className="text-sm italic text-gray-300">No contacts assigned</p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={activeContacts.map((c) => c.contactId)}
              strategy={horizontalListSortingStrategy}
            >
              <div className="flex gap-4 overflow-x-auto pb-2">
                {activeContacts.map((contact, index) => (
                  <SortableContactCard
                    key={contact.contactId}
                    contact={contact}
                    isPrimary={index === 0}
                    onDelete={() =>
                      setActiveContacts((list) =>
                        list.filter((c) => c.contactId !== contact.contactId),
                      )
                    }
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      <div className="flex flex-col justify-end gap-4 border-t border-gray-100 pt-4 md:flex-row">
        <Button
          text="Cancel"
          color="neutral"
          variant="outlined"
          size="fixed"
          icon={<CloseModal />}
          onClick={() => onClose?.()}
        />
        <Button
          text="Save Changes"
          color={isStaffMode ? "secondary" : "primary"}
          variant="filled"
          size="fixed"
          icon={<SaveChanges />}
          onClick={() => void handleSave()}
        />
      </div>
    </div>
  );
};

export default ManageOfficeMainContactsModal;
